import axios from 'axios';

// En desarrollo SIEMPRE /api (proxy Craco). En producción: REACT_APP_BACKEND_URL o mismo origen (/api).
function resolveApiBase() {
  if (process.env.NODE_ENV === 'development') return '/api';
  const configured = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
  if (!configured) return '/api';
  // Build antiguo con localhost:8001 pero SPA servida por uvicorn en :8000 u otro puerto local
  if (typeof window !== 'undefined') {
    const { hostname, port } = window.location;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
    if (isLocal && configured.includes('localhost:8001') && port && port !== '8001') {
      return '/api';
    }
  }
  return `${configured}/api`;
}

const API_BASE = resolveApiBase();

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors (no redirigir si el 401 viene del propio login/register, para poder mostrar el error)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAuthAttempt =
        url.includes('/auth/login') ||
        url.includes('/auth/register') ||
        url.includes('/auth/mfa/challenge');
      if (!isAuthAttempt) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        const path = typeof window !== 'undefined' ? window.location.pathname || '' : '';
        let loginPath = '/login';
        if (path.startsWith('/backoffice') || path.startsWith('/staff-login') || path.startsWith('/notarios') || path.startsWith('/soy-notario')) {
          loginPath = '/staff-login';
        } else if (path.startsWith('/portal-notario')) {
          loginPath = '/portal-notario/login';
        } else if (path.startsWith('/instituto')) {
          loginPath = '/instituto/login';
        }
        if (!path.startsWith(loginPath)) {
          window.location.href = loginPath;
        }
      }
    }
    return Promise.reject(error);
  }
);

// Users (para selección de destinatarios, etc.)
export const usersService = {
  getForDestinatary: (params) => api.get('/users/all-for-destinatary', { params }),
};

// Auth
export const authService = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  verifyCurp: () => api.post('/auth/verify-curp'),
  claimCitizen: (data) => api.post('/auth/claim-citizen', data),
  mfaStatus: () => api.get('/auth/mfa/status'),
  mfaSetup: (bearerToken) =>
    api.post(
      '/auth/mfa/setup',
      {},
      bearerToken ? { headers: { Authorization: `Bearer ${bearerToken}` } } : undefined
    ),
  mfaVerifyEnroll: (code, bearerToken) =>
    api.post(
      '/auth/mfa/verify-enroll',
      { code },
      bearerToken ? { headers: { Authorization: `Bearer ${bearerToken}` } } : undefined
    ),
  mfaChallenge: (code, mfaToken) =>
    api.post('/auth/mfa/challenge', { code }, {
      headers: { Authorization: `Bearer ${mfaToken}` },
    }),
  mfaDisable: (password, code) => api.post('/auth/mfa/disable', { password, code }),
};

// Timeout para subida de documentos (evita quedar "procesando" indefinidamente)
const UPLOAD_TIMEOUT_MS = 90000; // 90 segundos

// Vault
export const vaultService = {
  getDocTypes: () => api.get('/vault/doc-types'),
  upload: (formData) => api.post('/vault/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: UPLOAD_TIMEOUT_MS,
  }),
  getMyDocs: () => api.get('/vault/'),
  download: (docId) => api.get(`/vault/${docId}/download`, { responseType: 'blob' }),
  delete: (docId) => api.delete(`/vault/${docId}`),
};

// Procedures
export const procedureService = {
  list: (params) => api.get('/procedures/', { params }),
  listAll: () => api.get('/procedures/all'),
  get: (id) => api.get(`/procedures/${id}`),
  create: (data) => api.post('/procedures/', data),
  update: (id, data) => api.put(`/procedures/${id}`, data),
  publish: (id) => api.put(`/procedures/${id}/publish`),
  pause: (id) => api.put(`/procedures/${id}/pause`),
  resume: (id) => api.put(`/procedures/${id}/resume`),
  delete: (id) => api.delete(`/procedures/${id}`),
  consultarAdeudos: (data) => api.post('/procedures/consultar-adeudos', data),
};

// Requests
export const requestService = {
  create: (data) => api.post('/requests/', data),
  list: (params) => api.get('/requests/', { params }),
  get: (id) => api.get(`/requests/${id}`),
  downloadAcuse: (id) => api.get(`/requests/${id}/acuse`, { responseType: 'blob' }),
  respondPrevention: (id, formData) => api.post(`/requests/${id}/respond-prevention`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  uploadOficioEscaneado: (formData) => api.post('/requests/upload-oficio-escaneado', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Ventanilla Única: ciudadanos sin acceso digital
export const ventanillaService = {
  createOrFindCitizen: (data) => api.post('/ventanilla/ciudadanos', data),
  searchCitizens: (params) => api.get('/ventanilla/ciudadanos', { params }),
  getCitizen: (citizenId) => api.get(`/ventanilla/ciudadanos/${citizenId}`),
  listRequests: (citizenId) => api.get(`/ventanilla/ciudadanos/${citizenId}/requests`),
  createRequest: (citizenId, data) => api.post(`/ventanilla/ciudadanos/${citizenId}/requests`, data),
  listVault: (citizenId) => api.get(`/ventanilla/ciudadanos/${citizenId}/vault`),
  uploadVault: (citizenId, formData) => api.post(`/ventanilla/ciudadanos/${citizenId}/vault/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: UPLOAD_TIMEOUT_MS,
  }),
  downloadVault: (citizenId, docId) =>
    api.get(`/ventanilla/ciudadanos/${citizenId}/vault/${docId}/download`, { responseType: 'blob' }),
};

// Cases
export const caseService = {
  list: (params) => api.get('/cases/', { params }),
  get: (id) => api.get(`/cases/${id}`),
  update: (id, data) => api.put(`/cases/${id}`, data),
  issuePrevention: (id, data) => api.post(`/cases/${id}/prevention`, null, { params: data }),
  resolve: (id, data) => api.post(`/cases/${id}/resolve`, null, { params: data }),
  getRevisionCumplimiento: (id) => api.get(`/cases/${id}/revision-cumplimiento`),
  updateChecklist: (id, body) => api.patch(`/cases/${id}/checklist`, body),
  downloadAttachment: (caseId, attachmentKey) =>
    api.get(`/cases/${caseId}/attachments/${encodeURIComponent(attachmentKey)}/download`, { responseType: 'blob' }),
  iniciarRevisionCumplimiento: (id) => api.post(`/cases/${id}/iniciar-revision-cumplimiento`),
  aceptarCumplimiento: (id, data) => api.post(`/cases/${id}/aceptar-cumplimiento`, null, { params: data }),
  rechazarCumplimiento: (id, data) => api.post(`/cases/${id}/rechazar-cumplimiento`, null, { params: data }),
  getFlujoDocumental: (id) => api.get(`/cases/${id}/flujo-documental`),
};

// Documents
export const documentService = {
  list: (params) => api.get('/documents/', { params }),
  get: (id) => api.get(`/documents/${id}`),
  create: (data) => api.post('/documents/', data),
  importPdf: (formData) => api.post('/documents/import-pdf', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  update: (id, data) => api.put(`/documents/${id}`, null, { params: data }),
  generatePdf: (id) => api.post(`/documents/${id}/generate-pdf`),
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
};

// Clasificación archivística (Ley General de Archivos - CGCA y CDD)
export const clasificacionArchivisticaService = {
  listElementos: (params) => api.get('/clasificacion-archivistica/elementos', { params }),
  getArbol: () => api.get('/clasificacion-archivistica/elementos/arbol'),
  getElemento: (id) => api.get(`/clasificacion-archivistica/elementos/${id}`),
  getUsoElemento: (id) => api.get(`/clasificacion-archivistica/elementos/${id}/uso`),
  crearElemento: (data) => api.post('/clasificacion-archivistica/elementos', data),
  actualizarElemento: (id, data) => api.patch(`/clasificacion-archivistica/elementos/${id}`, data),
  eliminarElemento: (id) => api.delete(`/clasificacion-archivistica/elementos/${id}`),
  listDisposicion: () => api.get('/clasificacion-archivistica/disposicion'),
  crearDisposicion: (data) => api.post('/clasificacion-archivistica/disposicion', data),
  actualizarDisposicion: (id, data) => api.patch(`/clasificacion-archivistica/disposicion/${id}`, data),
  listFichas: () => api.get('/clasificacion-archivistica/fichas-tecnicas'),
  guardarFicha: (data) => api.post('/clasificacion-archivistica/fichas-tecnicas', data),
  seedDefaults: () => api.post('/clasificacion-archivistica/seed-defaults'),
  seedEjemplo: () => api.post('/clasificacion-archivistica/seed-ejemplo'),
};

// Asistente / Tutor IA (por rol del usuario logueado)
export const asistenteService = {
  getContexto: () => api.get('/asistente/contexto'),
  preguntar: (pregunta, perfil_agente = 'general', contexto_adicional = null) =>
    api.post('/asistente/pregunta', { pregunta, perfil_agente, contexto_adicional }),
};

// Búsqueda Universal Multientidad
export const busquedaService = {
  universal: (q, tipo = 'todos', limite = 25) =>
    api.get('/busqueda/universal', { params: { q, tipo, limite } }),
};

// Expedientes
export const expedienteService = {
  list: (params) => api.get('/expedientes/', { params }),
  get: (id) => api.get(`/expedientes/${id}`),
  create: (data) => api.post('/expedientes/', data),
  update: (id, data) => api.patch(`/expedientes/${id}`, data),
  caratula: (id) => api.get(`/expedientes/${id}/caratula`),
  caratulaPdf: (id) => api.get(`/expedientes/${id}/caratula/pdf`, { responseType: 'blob' }),
  addDocument: (id, docId, tipoDocumento) => api.put(`/expedientes/${id}/add-document`, null, { 
    params: { document_id: docId, tipo_documento: tipoDocumento } 
  }),
  removeDocument: (id, docId) => api.put(`/expedientes/${id}/remove-document`, null, { params: { document_id: docId } }),
  close: (id, force = false) => api.put(`/expedientes/${id}/close`, null, { params: { force } }),
  // P3 uses the same close; classification is required server-side
  reopen: (id) => api.put(`/expedientes/${id}/reopen`),
  getDocumentosDetalle: (id) => api.get(`/expedientes/${id}/documentos-detalle`),
  // Compartidos conmigo
  compartidosConmigo: (params) => api.get('/expedientes/compartidos-conmigo', { params }),
  // Compartición
  compartir: (data) => api.post('/expedientes/compartir', data),
  comparticionesEnviadas: (params) => api.get('/expedientes/comparticiones/enviadas', { params }),
  comparticionesRecibidas: (params) => api.get('/expedientes/comparticiones/recibidas', { params }),
  revocarComparticion: (id) => api.delete(`/expedientes/comparticiones/${id}`),
  descargarArchivoCompartido: (id) => api.get(`/expedientes/comparticiones/${id}/archivo`, { responseType: 'blob' }),
  extenderComparticion: (id, nuevaFecha) => api.put(`/expedientes/comparticiones/${id}/extender`, null, { 
    params: { nueva_fecha_expiracion: nuevaFecha } 
  }),
};

// Tipos de Expediente
export const tipoExpedienteService = {
  list: (params) => api.get('/tipos-expediente/', { params }),
  get: (id) => api.get(`/tipos-expediente/${id}`),
  create: (data) => api.post('/tipos-expediente/', data),
  update: (id, data) => api.put(`/tipos-expediente/${id}`, data),
  delete: (id) => api.delete(`/tipos-expediente/${id}`),
  seedDefaults: () => api.post('/tipos-expediente/seed-defaults'),
};

// Escritorio Virtual
export const escritorioService = {
  getResumen: () => api.get('/escritorio/resumen'),
  // Bandejas
  getRecibidos: (params) => api.get('/escritorio/bandeja/recibidos', { params }),
  getEnviados: (params) => api.get('/escritorio/bandeja/enviados', { params }),
  getParaFirmar: (params) => api.get('/escritorio/bandeja/para-firmar', { params }),
  getParaRevisar: (params) => api.get('/escritorio/bandeja/para-revisar', { params }),
  getUrgentes: (params) => api.get('/escritorio/bandeja/urgentes', { params }),
  getSinAtender: (params) => api.get('/escritorio/bandeja/sin-atender', { params }),
  getEnProceso: (params) => api.get('/escritorio/bandeja/en-proceso', { params }),
  getPendientesEnvio: (params) => api.get('/escritorio/bandeja/pendientes-envio', { params }),
  getTurnados: (params) => api.get('/escritorio/bandeja/turnados', { params }),
  getCorrespondencia: (params) => api.get('/escritorio/bandeja/correspondencia', { params }),
  getMisExpedientes: (params) => api.get('/escritorio/bandeja/mis-expedientes', { params }),
  getCompartidosConmigo: (params) => api.get('/escritorio/bandeja/compartidos-conmigo', { params }),
  getMisComparticiones: (params) => api.get('/escritorio/bandeja/mis-comparticiones', { params }),
  getCarpetaFirma: (params) => api.get('/escritorio/bandeja/carpeta-firma', { params }),
  getSinContestar: (params) => api.get('/escritorio/bandeja/sin-contestar', { params }),
  getVencidos: (params) => api.get('/escritorio/bandeja/vencidos', { params }),
  getCuentaDia: (params) => api.get('/escritorio/bandeja/cuenta-dia', { params }),
  darCuenta: (params) => api.post('/escritorio/dar-cuenta', null, { params }),
  marcarCuentaVista: (id) => api.post(`/escritorio/cuenta/${id}/vista`),
  getCompromisos: () => api.get('/escritorio/compromisos'),
  getAgenda: () => api.get('/escritorio/agenda'),
  crearAgenda: (params) => api.post('/escritorio/agenda', null, { params }),
  eliminarAgenda: (id) => api.delete(`/escritorio/agenda/${id}`),
  // Búsqueda
  busquedaEjecutiva: (q) => api.get('/escritorio/buscar/ejecutiva', { params: { q } }),
  busquedaAvanzada: (params) => api.get('/escritorio/buscar/avanzada', { params }),
  // Acciones buzón
  marcarLeido: (id) => api.put(`/escritorio/buzon/${id}/marcar-leido`),
  archivar: (id) => api.put(`/escritorio/buzon/${id}/archivar`),
  acusarRecibo: (id) => api.post(`/escritorio/buzon/${id}/acuse`),
  getAgenda: () => api.get('/escritorio/agenda'),
  crearAgenda: (params) => api.post('/escritorio/agenda', null, { params }),
  eliminarAgenda: (id) => api.delete(`/escritorio/agenda/${id}`),
};

// Inbox
export const inboxService = {
  send: (data) => api.post('/inbox/', data),
  listThreads: (params) => api.get('/inbox/threads', { params }),
  getThread: (id) => api.get(`/inbox/threads/${id}`),
  toggleFavorite: (id) => api.post(`/inbox/threads/${id}/favorite`),
  getUnreadCount: () => api.get('/inbox/unread-count'),
};

// Admin
export const adminService = {
  createArea: (data) => api.post('/admin/areas', data),
  listAreas: (params) => api.get('/admin/areas', { params }),
  getArea: (id) => api.get(`/admin/areas/${id}`),
  updateArea: (id, data) => api.put(`/admin/areas/${id}`, data),
  deleteArea: (id) => api.delete(`/admin/areas/${id}`),
  getAreaHierarchy: (id) => api.get(`/admin/areas/${id}/hierarchy`),
  uploadAreaLogo: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/areas/${id}/letterhead/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadAreaSello: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/areas/${id}/letterhead/sello`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  listUsers: (params) => api.get('/admin/users', { params }),
  /** Usuarios asignables a casos: área + subáreas. Para operadores/revisores/titular. */
  listAssignableUsers: (areaId, params) => api.get(`/admin/areas/${areaId}/users-asignables`, { params: params || {} }),
  updateUserRole: (id, role, areaId) => api.put(`/admin/users/${id}/role`, null, { params: { role, area_id: areaId } }),
  toggleUserActive: (id) => api.put(`/admin/users/${id}/toggle-active`),
  getCitizenDashboard: () => api.get('/admin/dashboard/citizen'),
  getBackofficeDashboard: () => api.get('/admin/dashboard/backoffice'),
  /** Indica si seed-dev está permitido (público; false en production). */
  getSeedStatus: () => api.get('/admin/seed-status'),
  seed: () => api.post('/admin/seed'),
  /** Seed de desarrollo (sin auth). Crea usuarios de prueba si la BD está vacía o ALLOW_DEV_SEED=1. */
  /** Seed completo; puede tardar ~30s — timeout extendido. */
  seedDev: () => api.post('/admin/seed-dev', {}, { timeout: 120000 }),
  /** Actualiza contraseñas de usuarios de prueba en la BD (ALLOW_DEV_SEED=1 o BD vacía). */
  syncSeedPasswords: () => api.post('/admin/sync-seed-passwords'),
  /** Verifica que los usuarios de prueba existan y que las contraseñas coincidan (ALLOW_DEV_SEED=1 o BD vacía). */
  verifySeedUsers: () => api.get('/admin/verify-seed-users'),
  /** Precarga trámites por defecto (sin auth en desarrollo). Crea áreas si faltan. */
  seedProcedures: () => api.post('/admin/seed-procedures'),
  getGovernanceAccess: (tenantId) => api.get('/admin/governance-access', { params: tenantId ? { tenant_id: tenantId } : {} }),
  updateGovernanceAccess: (data, tenantId) => api.put('/admin/governance-access', data, { params: tenantId ? { tenant_id: tenantId } : {} }),
};

// Correspondencia
export const correspondenciaService = {
  crear: (data) => api.post('/correspondencia/', data),
  enviar: (formData) => api.post('/correspondencia/enviar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  listar: (params) => api.get('/correspondencia/', { params }),
  obtener: (id) => api.get(`/correspondencia/${id}`),
  verPDF: (id) => api.get(`/correspondencia/${id}/pdf`, { responseType: 'blob' }),
  actualizarEstado: (id, estado) => api.put(`/correspondencia/${id}/estado`, null, { params: { estado } }),
  turnar: (id, usuarioId, instrucciones) => api.put(`/correspondencia/${id}/turnar`, null, { params: { usuario_id: usuarioId, instrucciones } }),
  bandejaEntrada: (params) => api.get('/correspondencia/bandeja/entrada', { params }),
  resumen: () => api.get('/correspondencia/stats/resumen'),
  acuseRecepcion: (id) => api.get(`/correspondencia/${id}/acuse-recepcion`, { responseType: 'blob' }),
};

// Oficios
export const oficioService = {
  crear: (data) => api.post('/oficios/', data),
  listar: (params) => api.get('/oficios/', { params }),
  obtener: (id) => api.get(`/oficios/${id}`),
  getUsuariosDestinatario: (params) => api.get('/oficios/usuarios-destinatario', { params }),
  actualizar: (id, data) => api.put(`/oficios/${id}`, data),
  cambiarEstado: (id, estado) => api.put(`/oficios/${id}/estado`, null, { params: { estado } }),
  // Flujo de creación
  getAreasDisponibles: () => api.get('/oficios/areas-disponibles'),
  getAreasOrganizacion: () => api.get('/oficios/areas-organizacion'),
  subirPdfFirmado: (formData) => api.post('/oficios/subir-pdf-firmado', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Flujo de aprobación
  enviarRevision: (id) => api.post(`/oficios/${id}/enviar-revision`),
  aprobar: (id, comentarios) => api.post(`/oficios/${id}/aprobar`, null, { params: { comentarios } }),
  rechazar: (id, motivo) => api.post(`/oficios/${id}/rechazar`, null, { params: { motivo } }),
  firmar: (id, params) => api.post(`/oficios/${id}/firmar`, {
    fecha_firma: params?.fecha_firma,
    firma_autografa: params?.firma_autografa,
    usar_efirma: params?.usar_efirma || false,
    efirma_password: params?.efirma_password || undefined,
  }, { params: params?.fecha_firma ? { fecha_firma: params.fecha_firma } : {} }),
  enviar: (id) => api.post(`/oficios/${id}/enviar`),
  // Listados
  misOficios: (params) => api.get('/oficios/mis-oficios', { params }),
  pendientesRevision: () => api.get('/oficios/pendientes-revision'),
  pendientesEnvio: () => api.get('/oficios/pendientes-envio'),
  posponerEnvio: (id) => api.post(`/oficios/${id}/posponer-envio`),
  agregarAnotacion: (id, data) => api.post(`/oficios/${id}/anotaciones`, data),
  agregarNotaInterna: (id, texto) => api.post(`/oficios/${id}/notas-internas`, { texto }),
  listarAnotaciones: (id) => api.get(`/oficios/${id}/anotaciones`),
  listarVersiones: (id) => api.get(`/oficios/${id}/versiones`),
  // Adjuntos
  subirAdjunto: (oficioId, formData) => api.post(`/oficios/${oficioId}/adjuntos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  eliminarAdjunto: (oficioId, adjuntoId) => api.delete(`/oficios/${oficioId}/adjuntos/${adjuntoId}`),
  descargarAdjunto: (oficioId, adjuntoId) => api.get(`/oficios/${oficioId}/adjuntos/${adjuntoId}/descargar`, { 
    responseType: 'blob' 
  }),
  // Historial
  getHistorial: (id) => api.get(`/oficios/${id}/historial`),
  // PDF con membrete
  verPDF: (id) => api.get(`/oficios/${id}/pdf`, { responseType: 'blob' }),
  descargarPDF: (id) => api.get(`/oficios/${id}/pdf/download`, { responseType: 'blob' }),
  // Membrete
  getMembrete: () => api.get('/oficios/config/membrete'),
  updateMembrete: (params) => api.put('/oficios/config/membrete', null, { params }),
  uploadLogo: (formData) => api.post('/oficios/config/membrete/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Firma electrónica Mifiel
  solicitarFirmaElectronica: (id, signatariosEmail) => api.post(
    `/oficios/${id}/solicitar-firma-electronica`, 
    null, 
    { params: { signatarios_email: signatariosEmail } }
  ),
  getEstadoFirma: (id) => api.get(`/oficios/${id}/estado-firma`),
  // Bandeja de oficios recibidos (usa endpoints de escritorio)
  bandejaRecibidos: (params) => api.get('/escritorio/bandeja/recibidos', { params }),
  bandejaDetalle: (buzonId) => api.get(`/escritorio/buzon/${buzonId}`),
  marcarLeido: (buzonId) => api.put(`/escritorio/buzon/${buzonId}/marcar-leido`),
  acusarRecibo: (buzonId) => api.post(`/escritorio/buzon/${buzonId}/acuse`),
  sinContestar: (params) => api.get('/oficios/sin-contestar', { params }),
  paraResponder: () => api.get('/oficios/para-responder'),
  carpetaFirma: () => api.get('/oficios/carpeta-firma'),
  toggleCarpetaFirma: (id, incluir = true, orden) => api.post(`/oficios/${id}/carpeta-firma`, null, { params: { incluir, orden } }),
  marcarEnterado: (id) => api.post(`/oficios/${id}/enterado`),
  enterados: (id) => api.get(`/oficios/${id}/enterados`),
  clasificar: (id, params) => api.post(`/oficios/${id}/clasificar`, null, { params }),
  cerrarAsunto: (id, params) => api.post(`/oficios/${id}/cerrar-asunto`, null, { params }),
  copiaCertificada: (id) => api.get(`/oficios/${id}/copia-certificada`, { responseType: 'blob' }),
  verificar: (codigo) => api.get(`/oficios/verificar/${codigo}`),
};

// Plantillas
export const plantillaService = {
  crear: (data) => api.post('/plantillas/', data),
  listar: (params) => api.get('/plantillas/', { params }),
  obtener: (id) => api.get(`/plantillas/${id}`),
  actualizar: (id, data) => api.put(`/plantillas/${id}`, data),
  eliminar: (id) => api.delete(`/plantillas/${id}`),
  renderizar: (plantillaId, variables) => api.post('/plantillas/render', variables, { params: { plantilla_id: plantillaId } }),
  cargarDefaults: () => api.post('/plantillas/seed-defaults'),
};

// Flujos de Trabajo 2.0
export const flujoService = {
  crear: (data) => api.post('/flujos/', data),
  listar: (params) => api.get('/flujos/', { params }),
  obtener: (id) => api.get(`/flujos/${id}`),
  actualizar: (id, data) => api.put(`/flujos/${id}`, data),
  clonar: (id, nuevoNombre) => api.post(`/flujos/${id}/clonar`, null, { params: { nuevo_nombre: nuevoNombre } }),
  catalogoPlantillas: () => api.get('/flujos/plantillas-proceso/catalogo'),
  iniciarInstancia: (flujoId, documentoId) => api.post('/flujos/instancia/iniciar', null, { params: { flujo_id: flujoId, documento_id: documentoId } }),
  obtenerInstancia: (id) => api.get(`/flujos/instancia/${id}`),
  avanzarPaso: (instanciaId, accion, comentarios) => api.post(`/flujos/instancia/${instanciaId}/avanzar`, null, { params: { accion, comentarios } }),
  misPendientes: (params) => api.get('/flujos/instancias/pendientes', { params }),
  cargarDefaults: () => api.post('/flujos/seed-defaults'),
  /** Copia todos los flujos estándar (admin) al área del usuario o a la indicada. */
  cargarEnArea: (areaId) => api.post('/flujos/cargar-en-area', null, { params: areaId ? { area_id: areaId } : {} }),
};

// Turnados
export const turnadoService = {
  crear: (data) => api.post('/turnados/', data),
  listar: (params) => api.get('/turnados/', { params }),
  obtener: (id) => api.get(`/turnados/${id}`),
  recibidos: (params) => api.get('/turnados/recibidos', { params }),
  enviados: (params) => api.get('/turnados/enviados', { params }),
  aceptar: (id) => api.put(`/turnados/${id}/aceptar`),
  marcarEnProceso: (id) => api.put(`/turnados/${id}/en-proceso`),
  completar: (id, respuesta) => api.put(`/turnados/${id}/completar`, null, { params: { respuesta } }),
  rechazar: (id, motivo) => api.put(`/turnados/${id}/rechazar`, null, { params: { motivo } }),
  resumen: () => api.get('/turnados/stats/resumen'),
  instrucciones: () => api.get('/turnados/instrucciones'),
  documentosDisponibles: (params) => api.get('/turnados/documentos-disponibles', { params }),
};

// Firmas Electrónicas
export const firmaService = {
  firmarDocumento: (documentoId, tipoFirma, cargo, signatureImage) => api.post(`/firmas/documento/${documentoId}/firmar`, null, { params: { tipo_firma: tipoFirma, cargo, signature_image: signatureImage } }),
  obtenerFirmas: (documentoId) => api.get(`/firmas/documento/${documentoId}`),
  configurarRequeridas: (documentoId, firmasRequeridas) => api.put(`/firmas/documento/${documentoId}/configurar-requeridas`, null, { params: { firmas_requeridas: firmasRequeridas } }),
  verificarPublica: (codigo) => api.get(`/firmas/verificar/${codigo}`),
  pendientes: (params) => api.get('/firmas/pendientes', { params }),
  tiposFirma: () => api.get('/firmas/tipos'),
  // Firma autógrafa
  firmaAutografa: (documentoId, imagenBase64, tipoFirma, cargo) => 
    api.post(`/firmas/documento/${documentoId}/firma-autografa`, null, { 
      params: { imagen_base64: imagenBase64, tipo_firma: tipoFirma, cargo } 
    }),
  miFirmaAutografa: () => api.get('/firmas/usuario/mi-firma-autografa'),
  guardarMiFirmaAutografa: (imagenBase64) => 
    api.post('/firmas/usuario/guardar-firma-autografa', null, { 
      params: { imagen_base64: imagenBase64 } 
    }),
  // e.firma (FIEL) local
  miEfirma: () => api.get('/firmas/usuario/mi-efirma'),
  guardarMiEfirma: (cerFile, keyFile, password) => {
    const fd = new FormData();
    fd.append('cer', cerFile);
    fd.append('key', keyFile);
    fd.append('password', password);
    return api.post('/firmas/usuario/mi-efirma', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  eliminarMiEfirma: () => api.delete('/firmas/usuario/mi-efirma'),
  validarPasswordEfirma: (password) => {
    const fd = new FormData();
    fd.append('password', password);
    return api.post('/firmas/usuario/mi-efirma/validar-password', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  validarEfirmaCiudadano: (cerFile, keyFile, password) => {
    const fd = new FormData();
    fd.append('cer', cerFile);
    fd.append('key', keyFile);
    fd.append('password', password);
    return api.post('/firmas/ciudadano/validar-efirma', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Notificaciones
export const notificacionService = {
  listar: (params, signal) => api.get('/notificaciones/', { params, signal }),
  contarNoLeidas: (signal) => api.get('/notificaciones/no-leidas/count', { signal }),
  marcarLeida: (id) => api.put(`/notificaciones/${id}/leer`),
  marcarTodasLeidas: () => api.put('/notificaciones/leer-todas'),
  eliminar: (id) => api.delete(`/notificaciones/${id}`),
  tipos: () => api.get('/notificaciones/tipos'),
  ejecutarRecordatorios: () => api.post('/notificaciones/ejecutar-recordatorios'),
};

// Actividad de Expedientes
export const actividadService = {
  obtenerPorExpediente: (expedienteId, params) => api.get(`/actividad/expediente/${expedienteId}`, { params }),
  obtenerMisExpedientes: (params) => api.get('/actividad/mis-expedientes', { params }),
  obtenerTipos: () => api.get('/actividad/tipos'),
};

// Preferencias de Usuario (Accesos Rápidos)
export const preferenciaService = {
  obtener: () => api.get('/preferencias/'),
  guardarAccesosRapidos: (accesos) => api.put('/preferencias/accesos-rapidos', { accesos_rapidos: accesos }),
  restaurarDefaults: () => api.post('/preferencias/restaurar'),
};

// Integridad de Archivos
export const integridadService = {
  verificar: (docId) => api.get(`/integridad/verificar/${docId}`),
  verificarExpediente: (expId) => api.get(`/integridad/verificar-expediente/${expId}`),
  registrarHash: (docId) => api.post(`/integridad/registrar-hash/${docId}`),
  auditoria: (docId, params) => api.get(`/integridad/auditoria/${docId}`, { params }),
  estadoGeneral: () => api.get('/integridad/estado-general'),
};

// Reportes y Exportación
export const reporteService = {
  correspondencia: (params) => api.get('/reportes/correspondencia', { params }),
  oficios: (params) => api.get('/reportes/oficios', { params }),
  turnados: (params) => api.get('/reportes/turnados', { params }),
  firmas: (params) => api.get('/reportes/firmas', { params }),
  dashboard: (params) => api.get('/reportes/dashboard', { params }),
  productividadUsuarios: (params) => api.get('/reportes/productividad/usuarios', { params }),
  tiemposRespuesta: (params) => api.get('/reportes/tiempos-respuesta', { params }),
  exportarCSV: (tipo, params) => api.get(`/reportes/exportar/csv/${tipo}`, { params, responseType: 'blob' }),
  exportarJSON: (tipo, params) => api.get(`/reportes/exportar/json/${tipo}`, { params, responseType: 'blob' }),
};

// Mifiel - Firmas Electrónicas con e.firma
export const mifielService = {
  getConfig: () => api.get('/mifiel/config'),
  createDocument: (formData) => api.post('/mifiel/documents/create', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getDocument: (id) => api.get(`/mifiel/documents/${id}`),
  listDocuments: (params) => api.get('/mifiel/documents', { params }),
  downloadSigned: (id, format = 'pdf') => api.get(`/mifiel/documents/${id}/download`, {
    params: { format },
    responseType: 'blob'
  }),
};

export const libroService = {
  listar: (params) => api.get('/libro/', { params }),
  resumen: () => api.get('/libro/resumen'),
  obtener: (id) => api.get(`/libro/${id}`),
};

export const archivoService = {
  vigencias: () => api.get('/archivo/vigencias'),
  inventario: (params) => api.get('/archivo/inventario', { params }),
  transferencias: (params) => api.get('/archivo/transferencias', { params }),
  proponerTransferencia: (expediente_ids, destino, observaciones) =>
    api.post('/archivo/transferencias', expediente_ids, { params: { destino, observaciones } }),
  ejecutarTransferencia: (id) => api.post(`/archivo/transferencias/${id}/ejecutar`),
  recibirTransferencia: (id) => api.post(`/archivo/transferencias/${id}/recibir`),
  crearActa: (expediente_ids, fundamento, dictamen, testigo_ids) =>
    api.post('/archivo/actas', expediente_ids, { params: { fundamento, dictamen, testigo_ids } }),
  actas: () => api.get('/archivo/actas'),
  actaPdf: (id) => api.get(`/archivo/actas/${id}/pdf`, { responseType: 'blob' }),
  // Archivo 2.0 extensiones
  getSIA: () => api.get('/archivo/sia/estructura'),
  updateSIA: (data) => api.put('/archivo/sia/estructura', data),
  getPADA: (anio) => api.get('/archivo/pada', { params: anio ? { anio } : {} }),
  crearPADA: (data) => api.post('/archivo/pada', data),
  avanzarAccionPADA: (padaId, accionId, porcentaje, estatus, comentario) =>
    api.put(`/archivo/pada/${padaId}/acciones/${accionId}/avance`, null, { params: { porcentaje, estatus, comentario } }),
  getGrupoInterdisciplinario: () => api.get('/archivo/grupo-interdisciplinario/integrantes'),
  getSesionesGI: () => api.get('/archivo/grupo-interdisciplinario/sesiones'),
  crearSesionGI: (data) => api.post('/archivo/grupo-interdisciplinario/sesiones', data),
  getPrestamos: (estatus) => api.get('/archivo/prestamos', { params: estatus ? { estatus } : {} }),
  crearPrestamo: (data) => api.post('/archivo/prestamos', data),
  devolverPrestamo: (prestamoId, observaciones) =>
    api.post(`/archivo/prestamos/${prestamoId}/devolver`, null, { params: { observaciones } }),
  getDiagnosticoLGA: () => api.get('/archivo/auditoria/diagnostico-lga'),
};

export const valijaService = {
  crear: (formData) => api.post('/valijas/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  listar: (params) => api.get('/valijas/', { params }),
  obtener: (id) => api.get(`/valijas/${id}`),
  enviar: (id) => api.post(`/valijas/${id}/enviar`),
  subirAcuse: (id, formData) => api.post(`/valijas/${id}/acuse`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  constancia: (id) => api.get(`/valijas/${id}/constancia`, { responseType: 'blob' }),
  acuseArchivo: (id) => api.get(`/valijas/${id}/acuse-archivo`, { responseType: 'blob' }),
};

// Instrucciones y Compromisos
export const instruccionesService = {
  listar: (params) => api.get('/instrucciones/', { params }),
  obtener: (id) => api.get(`/instrucciones/${id}`),
  crear: (data) => api.post('/instrucciones/', data),
  actualizar: (id, data) => api.put(`/instrucciones/${id}`, data),
  agregarSubtarea: (id, data) => api.post(`/instrucciones/${id}/subtareas`, data),
  actualizarSubtarea: (id, subtareaId, data) => api.put(`/instrucciones/${id}/subtareas/${subtareaId}`, data),
  agregarEvidencia: (id, data) => api.post(`/instrucciones/${id}/evidencias`, data),
  validarEvidencia: (id, evidenciaId, data) => api.post(`/instrucciones/${id}/evidencias/${evidenciaId}/validar`, data),
  delegar: (id, data) => api.post(`/instrucciones/${id}/delegar`, data),
  cerrar: (id, data) => api.post(`/instrucciones/${id}/cerrar`, data),
};

// Despacho Digital y Semáforo Institucional
export const despachoService = {
  resumen: () => api.get('/despacho/resumen'),
  semaforoInstitucional: () => api.get('/despacho/semaforo-institucional'),
  semaforoPermisos: () => api.get('/despacho/semaforo-permisos'),
};

// Acuerdos y Reuniones de Gabinete
export const acuerdosService = {
  listarReuniones: (params) => api.get('/acuerdos/reuniones', { params }),
  obtenerReunion: (id) => api.get(`/acuerdos/reuniones/${id}`),
  crearReunion: (data) => api.post('/acuerdos/reuniones', data),
  crearAcuerdo: (meetingId, data) => api.post(`/acuerdos/reuniones/${meetingId}/acuerdos`, data),
  procesarMinutaIA: (meetingId, data) => api.post(`/acuerdos/reuniones/${meetingId}/procesar-ia`, data),
};

// Entitlements & Features
export const entitlementsService = {
  current: () => api.get('/entitlements/current'),
  adminConfig: (tenantId) => api.get('/entitlements/admin/config', { params: tenantId ? { tenant_id: tenantId } : {} }),
  toggle: (data) => api.put('/entitlements/admin/toggle', data),
  getTemplates: (params) => api.get('/entitlements/admin/templates', { params }),
  getTemplateComparison: (family = 'Notaría Digital') => api.get('/entitlements/admin/templates/comparison', { params: { family } }),
  previewPlanChange: (data) => api.post('/entitlements/admin/plan-change-preview', data),
  changePlan: (data) => api.post('/entitlements/admin/change-plan', data),
  getMembershipHistory: (tenantId, limit = 50) => api.get('/entitlements/admin/membership-history', { params: { ...(tenantId ? { tenant_id: tenantId } : {}), limit } }),
  applyTemplate: (data) => api.post('/entitlements/admin/apply-template', data),
  updateLimits: (data) => api.put('/entitlements/admin/limits', data),
  updateTemplateLimits: (templateId, data) => api.put(`/entitlements/admin/templates/${templateId}/limits`, data),
  getUsage: (tenantId) => api.get('/entitlements/admin/usage', { params: tenantId ? { tenant_id: tenantId } : {} }),
  getAuditLogs: (tenantId, limit = 50) => api.get('/entitlements/admin/audit-logs', { params: { ...(tenantId ? { tenant_id: tenantId } : {}), limit } }),
  listTenants: () => api.get('/entitlements/admin/tenants'),
  getPlatformKpis: () => api.get('/entitlements/admin/platform-kpis'),
  createTenant: (data) => api.post('/entitlements/admin/tenants', data),
  updateTenant: (tenantId, data) => api.put(`/entitlements/admin/tenants/${tenantId}`, data),
  provisionLegalFramework: (tenantId, force = false) =>
    api.post(`/entitlements/admin/tenants/${tenantId}/provision-legal-framework`, null, {
      params: force ? { force: true } : undefined,
    }),
  deleteTenant: (tenantId) => api.delete(`/entitlements/admin/tenants/${tenantId}`),
};

export const usersAdminService = {
  createMembershipAccounts: (data) => api.post('/users/membership-accounts', data),
  changeOwnPassword: (data) => api.put('/users/me/password', data),
};

// Integration Hub e Interoperabilidad
// tenantId opcional: solo el superadmin de plataforma puede operar sobre otro cliente/membresía.
// La configuración de IA es global de plataforma (sin tenant_id).
const tenantParams = (tenantId, extra = {}) => ({
  ...extra,
  ...(tenantId ? { tenant_id: tenantId } : {}),
});

export const integracionService = {
  listarConectores: (tenantId) => api.get('/integraciones/conectores', { params: tenantParams(tenantId) }),
  crearConector: (data, tenantId) => api.post('/integraciones/conectores', data, { params: tenantParams(tenantId) }),
  actualizarConector: (sistemaId, data, tenantId) => api.put(`/integraciones/conectores/${sistemaId}`, data, { params: tenantParams(tenantId) }),
  eliminarConector: (sistemaId, tenantId) => api.delete(`/integraciones/conectores/${sistemaId}`, { params: tenantParams(tenantId) }),
  pingConector: (sistemaId, tenantId) => api.post(`/integraciones/conectores/${sistemaId}/ping`, null, { params: tenantParams(tenantId) }),
  listarApiKeys: (tenantId) => api.get('/integraciones/api-keys', { params: tenantParams(tenantId) }),
  crearApiKey: (data, tenantId) => api.post('/integraciones/api-keys', data, { params: tenantParams(tenantId) }),
  actualizarApiKey: (keyId, data, tenantId) => api.put(`/integraciones/api-keys/${keyId}`, data, { params: tenantParams(tenantId) }),
  revocarApiKey: (keyId, tenantId) => api.delete(`/integraciones/api-keys/${keyId}/revocar`, { params: tenantParams(tenantId) }),
  eliminarApiKey: (keyId, tenantId) => api.delete(`/integraciones/api-keys/${keyId}`, { params: tenantParams(tenantId) }),
  probarApiKey: (keyId, tenantId) => api.post(`/integraciones/api-keys/${keyId}/test`, null, { params: tenantParams(tenantId) }),
  listarWebhooks: (tenantId) => api.get('/integraciones/webhooks', { params: tenantParams(tenantId) }),
  registrarWebhook: (data, tenantId) => api.post('/integraciones/webhooks', data, { params: tenantParams(tenantId) }),
  actualizarWebhook: (whId, data, tenantId) => api.put(`/integraciones/webhooks/${whId}`, data, { params: tenantParams(tenantId) }),
  eliminarWebhook: (whId, tenantId) => api.delete(`/integraciones/webhooks/${whId}`, { params: tenantParams(tenantId) }),
  probarWebhook: (whId, evento, tenantId) => api.post(`/integraciones/webhooks/${whId}/test`, null, { params: tenantParams(tenantId, evento ? { evento_tipo: evento } : {}) }),
  getIdP: (tenantId) => api.get('/integraciones/identity-provider', { params: tenantParams(tenantId) }),
  updateIdP: (data, tenantId) => api.put('/integraciones/identity-provider', data, { params: tenantParams(tenantId) }),
  probarIdP: (tenantId) => api.post('/integraciones/identity-provider/test', null, { params: tenantParams(tenantId) }),
  mockAuthLlaveMx: (curp, nombre, email, tenantId) => api.post('/integraciones/identity-provider/llave-mx/mock-auth', null, { params: tenantParams(tenantId, { curp, nombre_completo: nombre, email }) }),
  listarConsentimientos: (tenantId) => api.get('/integraciones/expediente-ciudadano/consentimientos', { params: tenantParams(tenantId) }),
  otorgarConsentimiento: (data, tenantId) => api.post('/integraciones/expediente-ciudadano/consentimientos', data, { params: tenantParams(tenantId) }),
  revocarConsentimiento: (id, tenantId) => api.post(`/integraciones/expediente-ciudadano/consentimientos/${id}/revocar`, null, { params: tenantParams(tenantId) }),
  listarLogs: (limite = 50, tenantId) => api.get('/integraciones/logs', { params: tenantParams(tenantId, { limite }) }),
  // IA = solo plataforma / superadmin (global, sin tenant)
  getAiConfig: () => api.get('/integraciones/ia-config'),
  updateAiConfig: (data) => api.put('/integraciones/ia-config', data),
  testAiConfig: (data) => api.post('/integraciones/ia-config/test', data),
  deleteAiApiKey: () => api.delete('/integraciones/ia-config/api-key'),
};

// Cabildo Digital (Módulo Opcional)
export const cabildoService = {
  listarIntegrantes: () => api.get('/cabildo/integrantes/catalogo'),
  listarSesiones: (params) => api.get('/cabildo/sesiones', { params }),
  obtenerSesion: (id) => api.get(`/cabildo/sesiones/${id}`),
  getPermisos: () => api.get('/cabildo/permisos'),
  convocarSesion: (data) => api.post('/cabildo/sesiones', data),
  iniciarSesion: (id) => api.post(`/cabildo/sesiones/${id}/iniciar`),
  registrarVotacion: (sesionId, puntoId, data) => api.post(`/cabildo/sesiones/${sesionId}/puntos/${puntoId}/votacion`, data),
  clausurarSesion: (id) => api.post(`/cabildo/sesiones/${id}/clausurar`),
  obtenerExpediente: (id) => api.get(`/cabildo/sesiones/${id}/expediente`),
  listarActas: () => api.get('/cabildo/actas'),
  obtenerActa: (id) => api.get(`/cabildo/actas/${id}`),
  crearActa: (data) => api.post('/cabildo/actas', data),
  actualizarActa: (id, data) => api.put(`/cabildo/actas/${id}`, data),
  eliminarActa: (id) => api.delete(`/cabildo/actas/${id}`),
  subirArchivoActa: (id, file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    return api.post(`/cabildo/actas/${id}/archivo`, fd);
  },
  descargarArchivoActa: (id) => api.get(`/cabildo/actas/${id}/archivo`, { responseType: 'blob' }),
  eliminarArchivoActa: (id) => api.delete(`/cabildo/actas/${id}/archivo`),
  listarAcuerdos: (params) => api.get('/cabildo/acuerdos', { params }),
  obtenerAcuerdo: (id) => api.get(`/cabildo/acuerdos/${id}`),
  crearAcuerdo: (data) => api.post('/cabildo/acuerdos', data),
  actualizarAcuerdo: (id, data) => api.put(`/cabildo/acuerdos/${id}`, data),
  eliminarAcuerdo: (id) => api.delete(`/cabildo/acuerdos/${id}`),
  subirArchivoAcuerdo: (id, file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    return api.post(`/cabildo/acuerdos/${id}/archivo`, fd);
  },
  descargarArchivoAcuerdo: (id) => api.get(`/cabildo/acuerdos/${id}/archivo`, { responseType: 'blob' }),
  eliminarArchivoAcuerdo: (id) => api.delete(`/cabildo/acuerdos/${id}/archivo`),
};

// Experiencia Digital Ciudadana ("MI GOBIERNO")
export const citizenExperienceService = {
  getHome: () => api.get('/citizen/home'),
  getCategories: () => api.get('/citizen/categories'),
  resolveRequirements: (procedureId) => api.get(`/citizen/requirements/resolve/${procedureId}`),
  getProfileAutofill: () => api.get('/citizen/profile/autofill'),
  saveDraft: (data) => api.post('/citizen/drafts', data),
  getDraft: (procedureId) => api.get(`/citizen/drafts/${procedureId}`),
  deleteDraft: (procedureId) => api.delete(`/citizen/drafts/${procedureId}`),
  getQuePasa: (requestId) => api.get(`/citizen/requests/${requestId}/que-pasa`),
  getVaultCards: () => api.get('/citizen/vault/cards'),
  routeIntent: (query) => api.get(`/citizen/intent-router?q=${encodeURIComponent(query)}`),
  chatAssistant: (data) => api.post('/citizen/assistant/chat', data),
};

// Pagos y Pasarelas Multicanal
export const paymentService = {
  getMyPayments: () => api.get('/payments/my-payments'),
  getProceduresWithCost: () => api.get('/payments/procedures-with-cost'),
  getTransactions: () => api.get('/payments/transactions'),
  checkout: (data) => api.post('/payments/checkout', data),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  confirmSimulated: (sessionId) => api.post(`/payments/confirm-simulated?session_id=${sessionId}`),
  getReceipt: (sessionId) => api.get(`/payments/receipt/${sessionId}`),
};

// Reportes Ciudadanos e Incidencias Georreferenciadas
export const citizenReportService = {
  getCategories: () => api.get('/citizen/reports/categories'),
  createReport: (data) => api.post('/citizen/reports', data),
  getMyReports: () => api.get('/citizen/reports/my-reports'),
  getDetail: (id) => api.get(`/citizen/reports/${id}`),
  resolveReport: (id, data) => api.post(`/citizen/reports/${id}/resolve`, data),
};

// Identidades, Domicilios Múltiples, Organizaciones y Representaciones (Sprint 2)
export const citizenIdentitiesService = {
  getProfile: () => api.get('/citizen/identities/profile'),
  updateProfile: (data) => api.put('/citizen/identities/profile', data),
  getOrganizations: () => api.get('/citizen/identities/organizations'),
  createOrganization: (data) => api.post('/citizen/identities/organizations', data),
  getRepresentations: () => api.get('/citizen/identities/representations'),
  createRepresentation: (data) => api.post('/citizen/identities/representations', data),
  revokeRepresentation: (id) => api.post(`/citizen/identities/representations/${id}/revoke`),
  getActivity: (limit = 30) => api.get(`/citizen/identities/activity?limit=${limit}`),
};

// Casos Ciudadanos Unificados (Mis Solicitudes 2.0)
export const citizenCasesService = {
  listCases: (params) => api.get('/citizen/cases', { params }),
  createCase: (data, params) => api.post('/citizen/cases', data, { params }),
};

// Centro de Notificaciones y Preferencias
export const citizenNotificationsService = {
  listNotifications: (params) => api.get('/citizen/notifications', { params }),
  getUnreadCount: () => api.get('/citizen/notifications/unread-count'),
  markRead: (id) => api.post(`/citizen/notifications/${id}/read`),
  markAllRead: () => api.post('/citizen/notifications/read-all'),
  getPreferences: () => api.get('/citizen/notifications/preferences'),
  updatePreferences: (data) => api.put('/citizen/notifications/preferences', data),
};

// Citas Gubernamentales
export const citizenAppointmentsService = {
  getLocations: () => api.get('/citizen/appointments/locations'),
  getServices: () => api.get('/citizen/appointments/services'),
  getSlots: (serviceId, locationId, dateStr) =>
    api.get(`/citizen/appointments/slots?service_id=${serviceId}&location_id=${locationId}&date_str=${dateStr}`),
  createAppointment: (data) => api.post('/citizen/appointments', data),
  getMyAppointments: () => api.get('/citizen/appointments/my-appointments'),
  cancelAppointment: (id) => api.post(`/citizen/appointments/${id}/cancel`),
  verifyQr: (qrToken) => api.get(`/citizen/appointments/verify/${qrToken}`),
};

// Fila Virtual
export const citizenQueueService = {
  generateTicket: (data) => api.post('/citizen/queue/ticket', data),
  getMyTicket: () => api.get('/citizen/queue/my-ticket'),
  cancelTicket: (ticketId) => api.post(`/citizen/queue/ticket/${ticketId}/cancel`),
};

// Operaciones en Campo y Órdenes de Trabajo (Field Operations & Work Orders)
export const fieldOperationsService = {
  listCrews: (params) => api.get('/field-operations/crews', { params }),
  createCrew: (data) => api.post('/field-operations/crews', data),
  updateCrew: (id, data) => api.put(`/field-operations/crews/${id}`, data),
  updateCrewLocation: (id, lat, lng) =>
    api.put(`/field-operations/crews/${id}/location?latitude=${lat}&longitude=${lng}`),
  listWorkOrders: (params) => api.get('/field-operations/work-orders', { params }),
  createWorkOrder: (data) => api.post('/field-operations/work-orders', data),
  getWorkOrderDetail: (id) => api.get(`/field-operations/work-orders/${id}`),
  dispatchWorkOrder: (id, data) => api.post(`/field-operations/work-orders/${id}/dispatch`, data),
  startWorkOrder: (id, inTransit = false) =>
    api.post(`/field-operations/work-orders/${id}/start?in_transit=${inTransit}`),
  addEvidence: (id, data) => api.post(`/field-operations/work-orders/${id}/evidence`, data),
  toggleChecklist: (orderId, itemId, completed, notes) =>
    api.post(`/field-operations/work-orders/${orderId}/checklist/${itemId}?completed=${completed}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`),
  completeWorkOrder: (id, data) => api.post(`/field-operations/work-orders/${id}/complete`, data),
  getDashboard: () => api.get('/field-operations/dashboard'),
};

// Hub de Interoperabilidad Gubernamental Avanzada (RENAPO, Catastro, Agua y Sellado TSA)
export const interoperabilidadService = {
  validarCurpRenapo: (curp, purpose) =>
    api.post('/interoperabilidad/renapo/validar-curp', { curp, purpose }),
  consultarPredioCatastro: (claveCatastral, includeAdeudos = true) =>
    api.post('/interoperabilidad/catastro/consultar-predio', { clave_catastral: claveCatastral, include_adeudos: includeAdeudos }),
  consultarCuentaAgua: (numeroContrato, medidor) =>
    api.post('/interoperabilidad/agua/consultar-cuenta', { numero_contrato: numeroContrato, medidor }),
  estamparSelloResolucion: (data) =>
    api.post('/interoperabilidad/sellado/estampar-resolucion', data),
  verificarSello: (sealId) =>
    api.get(`/interoperabilidad/sellado/verificar/${sealId}`),
  getMetricas: () =>
    api.get('/interoperabilidad/metricas'),
};

// Panel de Analítica Territorial & Heatmaps Geoespaciales
export const geospatialService = {
  getSummary: () => api.get('/geospatial/summary'),
  getHeatmapPoints: (params) => api.get('/geospatial/heatmap', { params }),
  getSectors: () => api.get('/geospatial/sectors'),
  getCrewsLocations: () => api.get('/geospatial/crews-locations'),
  exportGeoJSON: (params) => api.get('/geospatial/export-geojson', { params }),
};

// Notaría Digital Integral (paquete notarial_operations)
export const notarialService = {
  dashboard: () => api.get('/notarial/dashboard'),
  securityCenter: () => api.get('/notarial/security/center'),
  complianceCenter: () => api.get('/notarial/compliance/center'),
  policyManual: () => api.get('/notarial/compliance/policy-manual'),
  listComplianceTrainings: () => api.get('/notarial/compliance/trainings'),
  listPldAudits: () => api.get('/notarial/compliance/audits'),
  recordPldAudit: (data) => api.post('/notarial/compliance/audits', data),
  runPldMonitoring: () => api.get('/notarial/compliance/monitoring'),
  listTaxRules: () => api.get('/notarial/tax/rules'),
  seedTaxRules: () => api.post('/notarial/tax/rules/seed'),
  overrideTaxCalculation: (id, data) => api.post(`/notarial/tax/isr-real-estate/${id}/override`, data),
  openSupportAccessSession: (data) => api.post('/notarial/support-access/sessions', data),
  listSupportAccessSessions: () => api.get('/notarial/support-access/sessions'),
  listTenantSupportAccessSessions: () => api.get('/notarial/support-access/tenant-sessions'),
  endSupportAccessSession: (id) => api.post(`/notarial/support-access/sessions/${id}/end`),
  listJurisdictions: () => api.get('/notarial/jurisdictions'),
  createJurisdiction: (data) => api.post('/notarial/jurisdictions', data),
  patchJurisdiction: (id, data) => api.patch(`/notarial/jurisdictions/${id}`, data),
  regulatoryRules: () => api.get('/notarial/regulatory-rules'),
  seedRegulatoryRules: () => api.post('/notarial/regulatory-rules/seed'),
  validationIssues: (matterId) => api.get(`/notarial/matters/${matterId}/validation-issues`),
  createLegalHold: (matterId, data) => api.post(`/notarial/matters/${matterId}/legal-holds`, data),
  listLegalHolds: (matterId) => api.get(`/notarial/matters/${matterId}/legal-holds`),
  releaseLegalHold: (holdId, data) => api.post(`/notarial/legal-holds/${holdId}/release`, data || {}),
  listNotaries: () => api.get('/notarial/notaries'),
  createNotary: (data) => api.post('/notarial/notaries', data),
  listActTypes: () => api.get('/notarial/act-types'),
  listParties: (params) => api.get('/notarial/parties', { params }),
  createParty: (data) => api.post('/notarial/parties', data),
  listMatters: (params) => api.get('/notarial/matters', { params }),
  createMatter: (data) => api.post('/notarial/matters', data),
  createIntakeSession: () => api.post('/notarial/matters/intake/sessions'),
  uploadIntakeFiles: (sessionId, formData) => api.post(`/notarial/matters/intake/${sessionId}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }),
  getIntakeSession: (sessionId) => api.get(`/notarial/matters/intake/${sessionId}`),
  updateIntakeSession: (sessionId, data) => api.patch(`/notarial/matters/intake/${sessionId}`, data),
  confirmIntakeSession: (sessionId, data) => api.post(`/notarial/matters/intake/${sessionId}/confirm`, data || {}),
  getMatterMissingData: (matterId) => api.get(`/notarial/matters/${matterId}/missing-data`),
  getMatter: (id) => api.get(`/notarial/matters/${id}`),
  getMatterCaratula: (id) => api.get(`/notarial/matters/${id}/caratula`),
  fePublica: () => api.get('/notarial/fe-publica'),
  updateMatter: (id, data) => api.patch(`/notarial/matters/${id}`, data),
  evaluateRules: (id, data) => api.post(`/notarial/matters/${id}/evaluate-rules`, data || {}),
  readyForSignature: (id) => api.post(`/notarial/matters/${id}/ready-for-signature`),
  completeMatter: (id) => api.post(`/notarial/matters/${id}/complete`),
  getLifecycleTransitions: (params) => api.get('/notarial/lifecycle/transitions', { params }),
  listInstruments: (matterId) => api.get(`/notarial/matters/${matterId}/instruments`),
  listMatterDocuments: (matterId) => api.get(`/notarial/matters/${matterId}/documents`),
  uploadMatterDocument: (matterId, formData) => api.post(`/notarial/matters/${matterId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  linkPlatformMatterDocument: (matterId, data) => api.post(`/notarial/matters/${matterId}/documents/from-platform`, data),
  downloadMatterDocumentUrl: (matterId, documentId) => `/api/notarial/matters/${matterId}/documents/${documentId}/download`,
  downloadMatterDocument: (matterId, documentId) => api.get(
    `/notarial/matters/${matterId}/documents/${documentId}/download`,
    { responseType: 'blob' },
  ),
  listAllInstruments: (params) => api.get('/notarial/instruments', { params }),
  createInstrument: (matterId, data) => api.post(`/notarial/matters/${matterId}/instruments`, data),
  authorizeInstrument: (id) => api.post(`/notarial/instruments/${id}/authorize`),
  updateInstrument: (id, data) => api.patch(`/notarial/instruments/${id}`, data),
  deleteInstrument: (id) => api.delete(`/notarial/instruments/${id}`),
  getInstrument: (id) => api.get(`/notarial/instruments/${id}`),
  signInstrument: (id, data) => api.post(`/notarial/instruments/${id}/sign`, data || {}),
  attachSignedInstrumentPdf: (id, formData) => api.post(`/notarial/instruments/${id}/attach-signed-pdf`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  createTestimony: (id, data) => api.post(`/notarial/instruments/${id}/testimony`, data || {}),
  listInstrumentTemplates: (params) => api.get('/notarial/instrument-templates', { params }),
  createInstrumentTemplate: (data) => api.post('/notarial/instrument-templates', data),
  getInstrumentTemplate: (id) => api.get(`/notarial/instrument-templates/${id}`),
  updateInstrumentTemplate: (id, data) => api.patch(`/notarial/instrument-templates/${id}`, data),
  createInstrumentTemplateVersion: (id, data) => api.post(`/notarial/instrument-templates/${id}/versions`, data || {}),
  listInstrumentTemplateVersions: (id) => api.get(`/notarial/instrument-templates/${id}/versions`),
  submitInstrumentTemplateReview: (id) => api.post(`/notarial/instrument-templates/${id}/submit-review`),
  approveInstrumentTemplate: (id) => api.post(`/notarial/instrument-templates/${id}/approve`),
  archiveInstrumentTemplate: (id) => api.post(`/notarial/instrument-templates/${id}/archive`),
  deleteInstrumentTemplate: (id) => api.delete(`/notarial/instrument-templates/${id}`),
  duplicateInstrumentTemplate: (id, data) => api.post(`/notarial/instrument-templates/${id}/duplicate`, data || {}),
  previewInstrumentTemplate: (id, data) => api.post(`/notarial/instrument-templates/${id}/preview`, data || {}),
  aiReviewInstrumentTemplate: (id) => api.post(`/notarial/instrument-templates/${id}/ai-review`),
  aiReviewInstrument: (id) => api.post(`/notarial/instruments/${id}/ai-review`),
  decideAiReviewFinding: (reviewId, findingId, data) => api.post(
    `/notarial/ai-reviews/${reviewId}/findings/${findingId}/decide`,
    data || {},
  ),
  listProtocolBooks: () => api.get('/notarial/protocol/books'),
  createProtocolBook: (data) => api.post('/notarial/protocol/books', data),
  listProtocolFolios: (params) => api.get('/notarial/protocol/folios', { params }),
  avisoEntidadProtocolo: (params) => api.get('/notarial/protocol/aviso-entidad', { params }),
  getMatterProtocolo: (matterId) => api.get(`/notarial/matters/${matterId}/protocolo`),
  listCotejos: () => api.get('/notarial/cotejos'),
  crearCotejo: (data) => api.post('/notarial/cotejos', data),
  listFeHechos: () => api.get('/notarial/fe-hechos'),
  crearFeHechos: (data) => api.post('/notarial/fe-hechos', data),
  cerrarProtocolBook: (bookId, data) => api.post(`/notarial/protocol/books/${bookId}/cerrar`, data),
  agregarApendice: (bookId, data) => api.post(`/notarial/protocol/books/${bookId}/apendice`, data),
  notaMarginal: (folioId, data) => api.post(`/notarial/protocol/folios/${folioId}/nota-marginal`, data),
  inutilizarFolio: (folioId, data) => api.post(`/notarial/protocol/folios/${folioId}/inutilizar`, data),
  listTestimonies: () => api.get('/notarial/testimonies'),
  entregarTestimonio: (testimonyId, data) => api.post(`/notarial/testimonies/${testimonyId}/entregar`, data),
  pldAssess: (data) => api.post('/notarial/pld/assess', data),
  pldRules: () => api.get('/notarial/pld/rules'),
  listPldAuditLogs: (params) => api.get('/notarial/pld/audit-logs', { params }),
  getPldAuditLog: (logId) => api.get(`/notarial/pld/audit-logs/${logId}`),
  downloadPldPepCedula: (logId) => api.get(`/notarial/pld/audit-logs/${logId}/cedula.pdf`, {
    responseType: 'blob',
  }),
  pepReviewReadiness: (matterId) => api.get(`/notarial/pld/pep-review/${matterId}/readiness`),
  pepReviewUpdateParties: (matterId, data) => api.patch(`/notarial/pld/pep-review/${matterId}/parties`, data),
  pepReviewRun: (matterId, data) => api.post(`/notarial/pld/pep-review/${matterId}/run`, data || {}),
  pepReviewCedulaPdf: (matterId, params) => api.get(`/notarial/pld/pep-review/${matterId}/cedula.pdf`, {
    params,
    responseType: 'blob',
  }),
  listTenantPldBlocks: (params) => api.get('/notarial/pld/blocks', { params }),
  listMatterPldBlocks: (matterId) => api.get(`/notarial/matters/${matterId}/pld-blocks`),
  listPldBlocks: (workflowInstanceId) => api.get(`/notarial/workflows/${workflowInstanceId}/pld-blocks`),
  preparePldClearance: (workflowInstanceId, params) =>
    api.post(`/notarial/workflows/${workflowInstanceId}/pld-clearance/prepare`, null, { params }),
  submitPldClearance: (workflowInstanceId, data, idempotencyKey) =>
    api.post(`/notarial/workflows/${workflowInstanceId}/pld-clearance/submit`, data, {
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    }),
  submitPldClearanceMifiel: (workflowInstanceId, data) =>
    api.post(`/notarial/workflows/${workflowInstanceId}/pld-clearance/mifiel`, data),
  postPldClearance: (workflowInstanceId, data, idempotencyKey) =>
    api.post(`/notarial/workflows/${workflowInstanceId}/pld-clearance`, data, {
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    }),
  taxIsrRealEstate: (data) => api.post('/notarial/tax/isr-real-estate', data),
  getIsrEnajenacion: (matterId) => api.get(`/notarial/matters/${matterId}/isr-enajenacion`),
  guardarIsrEnajenacion: (matterId, data) => api.post(`/notarial/matters/${matterId}/isr-enajenacion`, data),
  listQuotes: () => api.get('/notarial/quotes'),
  createQuote: (data) => api.post('/notarial/quotes', data),
  getQuote: (id) => api.get(`/notarial/quotes/${id}`),
  updateQuote: (id, data) => api.patch(`/notarial/quotes/${id}`, data),
  cancelQuote: (id) => api.post(`/notarial/quotes/${id}/cancel`),
  quotePdf: (id) => api.get(`/notarial/quotes/${id}/pdf`, { responseType: 'blob' }),
  listQuotePayments: (id) => api.get(`/notarial/quotes/${id}/payments`),
  addQuotePayment: (id, data) => api.post(`/notarial/quotes/${id}/payments`, data),
  listNotarialPayments: (params) => api.get('/notarial/payments', { params }),
  listInvoices: () => api.get('/notarial/invoices'),
  getInvoice: (id) => api.get(`/notarial/invoices/${id}`),
  createInvoiceFromQuote: (quoteId, data) => api.post(`/notarial/quotes/${quoteId}/invoice`, data || {}),
  updateInvoice: (id, data) => api.patch(`/notarial/invoices/${id}`, data),
  enrichInvoice: (id, data) => api.post(`/notarial/invoices/${id}/enrich`, data || {}),
  getInvoiceReadiness: (id) => api.get(`/notarial/invoices/${id}/readiness`),
  stampInvoice: (id) => api.post(`/notarial/invoices/${id}/stamp`),
  manualStampInvoice: (id, data) => api.post(`/notarial/invoices/${id}/manual-stamp`, data || {}),
  listObligations: (matterId) => api.get(`/notarial/matters/${matterId}/obligations`),
  listAllObligations: (params) => api.get('/notarial/obligations', { params }),
  generateObligations: (matterId) => api.post(`/notarial/matters/${matterId}/obligations/generate`),
  completeObligation: (id, data) => api.post(`/notarial/obligations/${id}/complete`, data || {}),
  getMatterPld: (matterId) => api.get(`/notarial/matters/${matterId}/pld`),
  listRegulatoryVault: (matterId) => api.get(`/notarial/matters/${matterId}/regulatory-vault`),
  addRegulatoryVaultEntry: (matterId, data) => api.post(`/notarial/matters/${matterId}/regulatory-vault`, data),
  getRegulatoryVaultEntry: (matterId, entryId) => api.get(`/notarial/matters/${matterId}/regulatory-vault/${entryId}`),
  updateRegulatoryVaultEntry: (matterId, entryId, data) => api.patch(`/notarial/matters/${matterId}/regulatory-vault/${entryId}`, data),
  deleteRegulatoryVaultEntry: (matterId, entryId) => api.delete(`/notarial/matters/${matterId}/regulatory-vault/${entryId}`),
  listBeneficialControllers: (matterId) => api.get(`/notarial/matters/${matterId}/beneficial-controllers`),
  addBeneficialController: (matterId, data) => api.post(`/notarial/matters/${matterId}/beneficial-controllers`, data),
  listPrivacyNotices: () => api.get('/notarial/privacy/notices'),
  getPrivacyProfile: () => api.get('/notarial/privacy/profile'),
  createPrivacyConsent: (data) => api.post('/notarial/privacy/consent', data),
  createArcoRequest: (data) => api.post('/notarial/privacy/arco', data),
  listArcoRequests: () => api.get('/notarial/privacy/arco'),
  logDataAccess: (data) => api.post('/notarial/privacy/access-log', data),
  listHelpTerms: (params) => api.get('/notarial/help/terms', { params }),
  getHelpTerm: (term, params) => api.get(`/notarial/help/terms/${encodeURIComponent(term)}`, { params }),
  seedHelpTerms: () => api.post('/notarial/help/seed'),
  listMatterParties: (matterId) => api.get(`/notarial/matters/${matterId}/matter-parties`),
  createMatterParty: (matterId, data) => api.post(`/notarial/matters/${matterId}/matter-parties`, data),
  updateMatterParty: (matterId, linkId, data) => api.patch(`/notarial/matters/${matterId}/matter-parties/${linkId}`, data),
  cotejoIdentidad: (matterId, linkId, data) => api.post(
    `/notarial/matters/${matterId}/matter-parties/${linkId}/cotejo-identidad`,
    data,
  ),
  resolverPld: (matterId, data) => api.post(`/notarial/matters/${matterId}/pld-resolucion`, data),
  accesoCliente: (matterId, data) => api.post(`/notarial/matters/${matterId}/acceso-cliente`, data || {}),
  listPartyParticipations: (partyId) => api.get(`/notarial/parties/${partyId}/participations`),
  listNotarialClients: () => api.get('/notarial/clients'),
  createNotarialClient: (data) => api.post('/notarial/clients', data),
  listParticipationRoles: () => api.get('/notarial/participation-roles'),
  explainValidation: (matterId, code) => api.get(`/notarial/matters/${matterId}/why/${encodeURIComponent(code)}`),
  listRuleTraces: (matterId) => api.get(`/notarial/matters/${matterId}/rule-traces`),
  listRegulatoryRuleVersions: (params) => api.get('/notarial/regulatory-rule-versions', { params }),
  getPldClientFile: (matterId) => api.get(`/notarial/matters/${matterId}/pld-client-file`),
  openPldClientFile: (matterId, data) => api.post(`/notarial/matters/${matterId}/pld-client-file`, data || {}),
  patchPldClientFile: (fileId, data) => api.patch(`/notarial/pld-client-files/${fileId}`, data),
  migrateJurisdictionIdentity: () => api.post('/notarial/admin/migrate-jurisdiction-identity'),
  preparaDeclaraNot: (data) => api.post('/notarial/declaranot/prepare', data),
  listConnectors: () => api.get('/notarial/connectors'),
  // Administración de conectores notariales (CRUD + ping) — superadmin o admin/titular del tenant
  listConnectorsAdmin: (tenantId) => api.get('/notarial-connectors', { params: tenantParams(tenantId) }),
  upsertConnector: (connectorId, data, tenantId) => api.put(`/notarial-connectors/${connectorId}`, data, { params: tenantParams(tenantId) }),
  patchConnector: (connectorId, data, tenantId) => api.patch(`/notarial-connectors/${connectorId}`, data, { params: tenantParams(tenantId) }),
  resetConnector: (connectorId, tenantId) => api.delete(`/notarial-connectors/${connectorId}`, { params: tenantParams(tenantId) }),
  pingConnector: (connectorId, tenantId) => api.post(`/notarial-connectors/${connectorId}/ping`, null, { params: tenantParams(tenantId) }),
  getEntidad: () => api.get('/notarial/entidad'),
  elegirEntidad: (data) => api.post('/notarial/entidad', data),
  jurisdictionConfig: () => api.get('/notarial/jurisdiction/config'),
  /** @deprecated usar jurisdictionConfig */
  chiapasPilot: () => api.get('/notarial/jurisdiction/config'),
  reviewLegalSource: (sourceId, data) => api.post(`/notarial/jurisdiction-review/legal-sources/${encodeURIComponent(sourceId)}`, data),
  reviewRegulatoryRule: (ruleId, data) => api.post(`/notarial/jurisdiction-review/regulatory-rules/${encodeURIComponent(ruleId)}`, data),
  evaluateChiapasModeChange: (data) => api.post('/notarial/chiapas/protocol/mode-change/evaluate', data),
  resolveChiapasRegistry: (municipality) => api.get('/notarial/chiapas/registry/resolve', { params: { municipality } }),
  assertChiapasSealScope: (data) => api.post('/notarial/chiapas/digital-seal/assert-scope', data),
  calculateChiapasFees: (data) => api.post('/notarial/chiapas/fees/calculate', data || {}),
  aiAction: (data) => api.post('/notarial/ai/action', data),
  listAppointments: () => api.get('/notarial/appointments'),
  createAppointment: (data) => api.post('/notarial/appointments', data),
  portalMatters: () => api.get('/notarial/portal/matters'),
  portalMatterDetail: (id) => api.get(`/notarial/portal/matters/${id}`),
  portalAppointments: () => api.get('/notarial/portal/appointments'),
  portalStatusLabels: () => api.get('/notarial/portal/status-labels'),
  aiCatalog: () => api.get('/notarial/ai/catalog'),
  createAiSuggestion: (data) => api.post('/notarial/ai/suggestions', data),
  listAiSuggestions: (matterId) => api.get(`/notarial/matters/${matterId}/ai-suggestions`),
  reviewAiSuggestion: (id, data) => api.post(`/notarial/ai/suggestions/${id}/review`, data),
  deleteAiSuggestion: (id) => api.delete(`/notarial/ai/suggestions/${id}`),
  // Intercambio controlado con el Instituto (proyecciones, no publicación en Internet)
  listPublicMatters: (params) => api.get('/notarial/public-matters', { params }),
  preparePublicMatter: (data) => api.post('/notarial/public-matters/prepare', data),
  previewPublicMatter: (id) => api.get(`/notarial/public-matters/${id}/preview`),
  publishPublicMatter: (id) => api.post(`/notarial/public-matters/${id}/publish`),
  revokePublicMatter: (id) => api.post(`/notarial/public-matters/${id}/revoke`),
  getPublicMatter: (id) => api.get(`/notarial/public-matters/${id}`),
};

export const publicService = {
  listInstitutions: (vertical) => api.get('/public/institutions', { params: vertical ? { vertical } : {} }),
  getInstitution: (slug) => api.get(`/public/institutions/${encodeURIComponent(slug)}`),
};

export default api;
